'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Add, ArrowDown2, Minus } from 'iconsax-react';

// -- Types matching your JSON structure --
export interface CategoryNode {
    name: string;
    uri: string;
    count: number;
    databaseId: number;
    children?: {
        nodes: CategoryNode[];
    };
}

// -- Single Item Component (Recursive / Hierarchy) --
const CategoryItem = ({ category }: { category: CategoryNode }) => {
    const [isOpen, setIsOpen] = useState(false);

    // Safely access nodes, default to empty array if null
    const children = category.children?.nodes || [];
    const hasChildren = children.length > 0;

    const toggleOpen = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation(); // Stop event bubbling
        setIsOpen(!isOpen);
    };

    return (
        <>
            <div className='flex items-center justify-between group hover:bg-gray-50 hover:dark:bg-white/5 transition-colors rounded-lg pr-2'>
                {/* Parent Link */}
                <Link
                    href={category.uri}
                    className='flex-grow py-3 text-gray-700 dark:text-white/70 dark:hover:text-progress hover:text-progress transition-colors text-xs font-light flex items-center gap-2 '
                >
                    {category.name}
                    <span className='text-[10px] text-gray-400 font-light bg-gray-100 dark:bg-white/10 px-1.5 py-0.5 rounded-md'>
                        {category.count}
                    </span>
                </Link>

                {/* Toggle Button - Only renders if children exist */}
                {hasChildren && (
                    <button
                        onClick={toggleOpen}
                        className='p-3 text-gray-400 hover:text-progress transition-all cursor-pointer z-10'
                        aria-label='Toggle Subcategories'
                    >
                        {isOpen ? (
                            <Minus size={18} className='dark:stroke-white stroke-primary' />
                        ) : (
                            <Add className='dark:stroke-white stroke-primary' size={18} />
                        )}
                    </button>
                )}
            </div>

            {/* Children Container using Grid Transition for smooth animation */}
            {hasChildren && (
                <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                        isOpen ? 'grid-rows-[1fr] opacity-100 mb-2' : 'grid-rows-[0fr] opacity-0'
                    }`}
                >
                    <div className='overflow-hidden'>
                        <ul className='bg-gray-50/50 dark:bg-white/5 mr-3 border-r-2 border-progress/30 flex flex-col'>
                            {children.map((child) => (
                                <li key={child.databaseId}>
                                    <Link
                                        href={child.uri}
                                        className='flex items-center justify-between p-2 pr-2 text-xs text-gray-600 dark:text-gray-400 hover:text-progress hover:bg-progress/5 transition-all'
                                    >
                                        <span>{child.name}</span>
                                        {/* Child Count */}
                                        <span className='text-[10px] opacity-60 ml-2'>
                                            ({child.count})
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </>
    );
};

// -- Main Sidebar Component --
interface SidebarProps {
    initialCategories: CategoryNode[];
    mobile: boolean;
}

const CategorySidebar = ({ initialCategories, mobile }: SidebarProps) => {
    // State for the "Show More" (Limit 12) functionality
    const [showAll, setShowAll] = useState(false);

    // Filter out categories that don't belong in the sidebar if needed,
    // or just render the list passed from props.
    const categories = initialCategories || [];

    const hasHiddenItems = categories.length > 12;

    return (
        <div
            className={` ${!mobile && 'sticky bg-white dark:bg-white/5 border border-black/5 dark:border-white/10 rounded-2xl p-4 top-24 shadow-sm'}`}
        >
            {!mobile && (
                <div className='mb-6 flex border-b border-black/10 dark:border-white/10'>
                    <h3 className='border-b-2 pb-2 border-progress text-xl'>دسته‌بندی‌ها</h3>
                </div>
            )}

            <ul className='flex flex-col'>
                {categories.map((cat, index) => {
                    // Logic: Hide items after index 11 (12th item) unless showAll is true
                    const isHidden = index >= 12 && !showAll;

                    return (
                        <li
                            key={cat.databaseId}
                            className={`${isHidden ? 'hidden' : 'block'} border-b border-gray-100 last:border-0 dark:border-white/5`}
                        >
                            <CategoryItem category={cat} />
                        </li>
                    );
                })}
            </ul>

            {/* "Show More" Button for the main list */}
            {hasHiddenItems && (
                <div className='mt-4'>
                    <button
                        onClick={() => setShowAll(!showAll)}
                        className='w-full py-2 text-sm text-gray-500 hover:text-progress transition-colors flex items-center justify-center gap-2 group'
                    >
                        {showAll ? 'نمایش کمتر' : 'مشاهده همه'}
                        <ArrowDown2
                            size={16}
                            className={`transition-transform duration-300 group-hover:text-progress dark:stroke-white stroke-primary ${showAll ? 'rotate-180' : ''}`}
                        />
                    </button>
                </div>
            )}
        </div>
    );
};

export default CategorySidebar;
